import React, { useEffect, useRef, useState } from "react";
import { getTranslation } from "../../utils/translations";
import { getParcelWithProject } from "../../services/api";
import Lottie from "lottie-react";
import HeartIcon from "../Icons/HeartIcon";
import IframeModal from "../Common/IframeModal";
import PhotoGalleryModal from "../Common/PhotoGalleryModal";
import { useGlobalModals } from "../Common/GlobalModals";

import "./ParcelDetailsModal.css";

const ParcelDetailsModal = ({
  parcelData,
  projectData,
  language,
  isVisible,
  onClose,
  isLoading = false,
  hasError = false,
  isFavorited = false,
  isLoadingFavorite = false,
  onVirtualTour,
  onShare,
  onReserve,
  onToggleFavorite
}) => {
  const modalRef = useRef(null);

  // State for modals and animations
  const [iframeModalOpen, setIframeModalOpen] = useState(false);
  const [iframeUrl, setIframeUrl] = useState('');
  const [iframeTitle, setIframeTitle] = useState('');
  const [photoGalleryOpen, setPhotoGalleryOpen] = useState(false);
  const [blueprintGalleryOpen, setBlueprintGalleryOpen] = useState(false);
  const [blueprintImages, setBlueprintImages] = useState([]);
  const [blueprintTitle, setBlueprintTitle] = useState('');

  // Animation states
  const [tour360Animation, setTour360Animation] = useState(null);
  const [blueprint3DAnimation, setBlueprint3DAnimation] = useState(null);
  // Global modals: use VideoModal for unit videos
  const { openVideoModal } = useGlobalModals();

  const [blueprint2DAnimation, setBlueprint2DAnimation] = useState(null);
  const [photoGalleryAnimation, setPhotoGalleryAnimation] = useState(null);
  const [videoAnimation, setVideoAnimation] = useState(null);

  // API data state
  const [detailedParcelData, setDetailedParcelData] = useState(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState(false);

  // Remove click outside to close functionality to allow map interaction
  // useEffect(() => {
  //   const handleClickOutside = (event) => {
  //     if (isVisible && modalRef.current && !modalRef.current.contains(event.target)) {
  //       onClose();
  //     }
  //   };

  //   if (isVisible) {
  //     // Add event listener when modal is visible
  //     document.addEventListener('mousedown', handleClickOutside);
  //     document.addEventListener('touchstart', handleClickOutside);
  //   }

  //   // Cleanup event listeners
  //   return () => {
  //     document.removeEventListener('mousedown', handleClickOutside);
  //     document.removeEventListener('touchstart', handleClickOutside);
  //   };
  // }, [isVisible, onClose]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && isVisible) {
        onClose();
      }
    };

    if (isVisible) {
      document.addEventListener('keydown', handleEscKey);
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
    };
  }, [isVisible, onClose]);

  // Load animations when component mounts
  useEffect(() => {
    const loadAnimations = async () => {
      try {
        const [tour360, blueprint3D, blueprint2D, photoGallery, video] = await Promise.all([
          import('../../assets/animations/360-view-white.json'),
          import('../../assets/animations/8-3D Modeling_opt.json'),
          // import('../../assets/animations/plan-white-animation.json'),
          import('../../assets/animations/17 Blueprint_opt.json'), // Using same for 2D
          import('../../assets/animations/09-Photo gallery_opt.json'),
          import('../../assets/animations/video-white-animation.json')
        ]);

        setTour360Animation(tour360.default);
        setBlueprint3DAnimation(blueprint3D.default);
        setBlueprint2DAnimation(blueprint2D.default);
        setPhotoGalleryAnimation(photoGallery.default);
        setVideoAnimation(video.default);
      } catch (error) {
      }
    };

    loadAnimations();
  }, []);

  // Fetch detailed parcel data when modal opens
  useEffect(() => {
    const fetchParcelDetails = async () => {
      if (!isVisible || !parcelData?.id) {
        return;
      }

      setIsLoadingDetails(true);
      setDetailsError(false);

      try {
        const response = await getParcelWithProject(parcelData.id);
        const detailedData = response.data.data;

        setDetailedParcelData(detailedData);
      } catch (error) {
        console.error("Error fetching detailed parcel data:", error);
        setDetailsError(true);
      } finally {
        setIsLoadingDetails(false);
      }
    };

    fetchParcelDetails();
  }, [isVisible, parcelData?.id]);

  // Helper functions
  const formatPrice = (price) => {
    if (!price) return "N/A";
    return new Intl.NumberFormat("en-US").format(price);
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "available":
        return "#22c55e"; // green
      case "reserved":
        return "#f59e0b"; // amber
      case "sold":
        return "#ef4444"; // red
      default:
        return "#6b7280"; // gray
    }
  };

  const getStatusText = (status) => {
    if (!status) return language === "ar" ? "غير محدد" : "Unknown";

    const statusMap = {
      available: language === "ar" ? "متاح" : "Available",
      reserved: language === "ar" ? "محجوز" : "Reserved",
      sold: language === "ar" ? "مباع" : "Sold"
    };

    return statusMap[status.toLowerCase()] || status;
  };

  // Button handlers
  const handleFavoriteClick = () => {
    if (onToggleFavorite) {
      onToggleFavorite(parcelData);
    }
  };

  const handleVirtualTourClick = () => {
    const currentParcel = detailedParcelData || parcelData;
    const currentProject = currentParcel?.project || projectData;
    // Try unit virtual tour first, then fallback to project
    const virtualTourUrl = currentParcel?.unit_virtual_tour_url || currentProject?.virtual_tour_url;
    if ( !virtualTourUrl) {
      return;
    }
    setIframeUrl(virtualTourUrl);
    setIframeTitle(language === "ar" ? "جولة افتراضية 360°" : "360° Virtual Tour");
    setIframeModalOpen(true);
  };

  const handleUnitVideoClick = () => {
    const currentParcel = detailedParcelData || parcelData;
    const currentProject = currentParcel?.project || projectData;
    // Try unit video first, then fallback to project
    const videoUrl = currentParcel?.unit_video_url || currentProject?.promotional_video_url;
    if ( !videoUrl) {
      return;
    }
    const title = language === "ar" ? "فيديو الوحدة" : "Unit Video";
    openVideoModal(videoUrl, title, language);
  };

  const handle3DBlueprintClick = () => {
    const currentParcel = detailedParcelData || parcelData;
    if ( !currentParcel?.blueprint_3d_url) {
      return;
    }
    const blueprint3DUrl = currentParcel.blueprint_3d_url;
    const title = language === "ar" ? "مخطط ثلاثي الأبعاد" : "3D Blueprint";
    const isImage = /\.(png|jpe?g|webp|gif)$/i.test(blueprint3DUrl);
    if (isImage) {
      setBlueprintImages([blueprint3DUrl]);
      setBlueprintTitle(title);
      setBlueprintGalleryOpen(true);
    } else {
      setIframeUrl(blueprint3DUrl);
      setIframeTitle(title);
      setIframeModalOpen(true);
    }
    };

  const handle2DBlueprintClick = () => {
    const currentParcel = detailedParcelData || parcelData;

    // Check if we have either URL or image
    const hasUrl = currentParcel?.blueprint_2d_url;
    const hasImage = currentParcel?.blueprint_2d_image_url;

    if (  (!hasUrl && !hasImage)) {
      return;
    }

    const title = language === "ar" ? "مخطط ثنائي الأبعاد" : "2D Blueprint";

    // Prefer image over URL for better viewing experience
    if (hasImage) {
      setBlueprintImages([currentParcel.blueprint_2d_image_url]);
      setBlueprintTitle(title);
      setBlueprintGalleryOpen(true);
    } else if (hasUrl) {
      setIframeUrl(currentParcel.blueprint_2d_url);
      setIframeTitle(title);
      setIframeModalOpen(true);
    }
  };

  const handlePhotoGalleryClick = () => {
    const currentParcel = detailedParcelData || parcelData;
    if (!currentParcel?.unit_photo_gallery_image_urls || currentParcel.unit_photo_gallery_image_urls.length === 0) {
      return;
    }
    setPhotoGalleryOpen(true);
  };

  const handleContractDocumentClick = () => {
    const currentParcel = detailedParcelData || parcelData;
    if ( (!currentParcel?.contract_document_url && !currentParcel?.contract_document)) {
      return;
    }
    const contractUrl = currentParcel.contract_document_url || currentParcel.contract_document;
    window.open(contractUrl, '_blank');
  };

  const handleCloseIframeModal = () => {
    setIframeModalOpen(false);
    setIframeUrl('');
    setIframeTitle('');
  };

  const handleClosePhotoGallery = () => {
    setPhotoGalleryOpen(false);
  };

  const handleCloseBlueprintGallery = () => {
    setBlueprintGalleryOpen(false);
    setBlueprintImages([]);
    setBlueprintTitle('');
  };

  if (!parcelData || !isVisible) {
    return null;
  }

  // Use detailed data if available, otherwise fallback to initial data
  const currentParcelData = detailedParcelData || parcelData;
  const currentProjectData = detailedParcelData?.project || projectData;

  return (
    <div className={`parcel-details-modal-overlay ${isVisible ? "visible" : ""}`}>
      <div
        ref={modalRef}
        className={`parcel-details-modal ${isVisible ? "visible" : ""} ${language === "ar" ? "rtl" : "ltr"}`}
        dir={language === "ar" ? "rtl" : "ltr"}
      >
        {/* Close Button */}
        <button className="modal-close-btn" onClick={onClose}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        {/* Loading Indicator */}
        {isLoadingDetails && (
          <div className="loading-indicator" style={{
            position: 'absolute',
            top: '15px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            fontSize: '12px',
            color: '#666',
            zIndex: 1000,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '4px 8px',
            borderRadius: '12px',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}>
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none">
              <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.3"/>
              <path d="M12 2 A10 10 0 0 1 22 12" stroke="currentColor" strokeWidth="2" fill="none">
                <animateTransform
                  attributeName="transform"
                  attributeType="XML"
                  type="rotate"
                  from="0 12 12"
                  to="360 12 12"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </path>
            </svg>
            {language === "ar" ? "تحديث البيانات..." : "Updating..."}
          </div>
        )}

        {/* Drag Handle */}
        {/* <div className="drag-handle"></div> */}

        {/* New Card Design */}
        <div className="card-container">
          {/* Left Side - Details */}
          <div className="card-left">
            {/* Price Header */}
            <div className="price-header">
              <div className="price-info">
                <span>{language === "ar" ? "احجز بـ" : "Reserve for"}</span>
                <span className="price-amount-bold">
                  {formatPrice(currentParcelData.reservation_amount || 0)}
                </span>
                <span className="price-amount">
                  {language === "ar" ? "ريال" : "SAR"}
                </span>
              </div>
              <div
                className="availability-badge"
                style={{ backgroundColor: getStatusColor(currentParcelData.sales_status) }}
              >
                <span>{getStatusText(currentParcelData.sales_status)} - {currentParcelData.parcel_number}</span>
                {/* <span></span> */}
              </div>
            </div>

            <hr className="hr-divider" />

            {/* Down Payment Section */}
            <div className="price-section">
              <span className="text-bold">
                {language === "ar" ? "دفعة أولى" : "Down Payment"}
              </span>
              <span className="price-amount-bold">
                {formatPrice(currentParcelData.down_payment || currentParcelData.price * 0.2)}
              </span>
              <span className="price-amount-margin">
                {language === "ar" ? "ريال" : "SAR"}
              </span>
            </div>

            <hr className="hr-divider" />

            {/* Monthly Payment Section */}
            <div className="monthly-section">
              <span>{language === "ar" ? "قسط شهري" : "Monthly Payment"}</span>
              <span className="price-amount-bold">
                {formatPrice(currentParcelData.monthly_payment || 0)}
              </span>
              <span>{language === "ar" ? "ريال" : "SAR"}</span>
              <span className="monthly-installment">-</span>
              <span>
                {language === "ar" ? "لمدة 0 سنوات" : "for 0 years"}
              </span>
            </div>

            <hr className="hr-divider" />

            {/* Total Price Section */}
            <div className="total-price-section text-small">
              <span>{language === "ar" ? "السعر الإجمالي" : "Total Price"}</span>
              <span className="price-amount-bold">
                {formatPrice(currentParcelData.price)}
              </span>
              <span>{language === "ar" ? "ريال" : "SAR"}</span>
            </div>

            {/* Features Section */}
            <div className="features-section" dir="ltr">
              <div className="feature-item border-not-first">
                <div className="feature-icon-placeholder">
                  <img
                    src="/unit_items/measurement.svg"
                    alt="Area"
                    className="feature-icon"
                    style={{ filter: 'grayscale(100%) brightness(0.5)' }}
                  />
                </div>
                <p className="feature-text">{currentParcelData.area || "120"}m²</p>
              </div>
              <div className="feature-item border-not-first">
                <div className="feature-icon-placeholder">
                  <img
                    src="/unit_items/bed.svg"
                    alt="Bedrooms"
                    className="feature-icon"
                    style={{ filter: 'grayscale(100%) brightness(0.5)' }}
                  />
                </div>
                <p className="feature-text">{currentParcelData.bedrooms || "1"}</p>
              </div>
              <div className="feature-item border-not-first">
                <div className="feature-icon-placeholder">
                  <img
                    src="/unit_items/bath.svg"
                    alt="Bathrooms"
                    className="feature-icon"
                    style={{ filter: 'grayscale(100%) brightness(0.5)' }}
                  />
                </div>
                <p className="feature-text">{currentParcelData.bathrooms || "1"}</p>
              </div>
              <div className="feature-item border-not-first">
                <div className="feature-icon-placeholder">
                  <img
                    src="/unit_items/daisy-alt.svg"
                    alt="Garden"
                    className="feature-icon"
                    style={{ filter: 'grayscale(100%) brightness(0.5)' }}
                  />
                </div>
                <p className="feature-text">{currentParcelData.garden ? "✓" : "-"}</p>
              </div>
              <div className="feature-item border-not-first">
                <div className="feature-icon-placeholder">
                  <img
                    src="/unit_items/N.png"
                    alt="Direction"
                    className="feature-icon"
                    style={{ filter: 'grayscale(100%) brightness(0.5)' }}
                  />
                </div>
                <p className="feature-text">{currentParcelData.direction || "-"}</p>
              </div>
            </div>

            {/* Reserve Button */}
            {(detailedParcelData || parcelData)?.sales_status?.toLowerCase() === "available" && (
              <button
                className="reserve-button"
                onClick={() => onReserve && onReserve(detailedParcelData || parcelData)}
                disabled={isLoading || isLoadingDetails}
              >
                {isLoadingDetails ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" opacity="0.3"/>
                      <path d="M12 2 A10 10 0 0 1 22 12" stroke="currentColor" strokeWidth="2" fill="none">
                        <animateTransform
                          attributeName="transform"
                          attributeType="XML"
                          type="rotate"
                          from="0 12 12"
                          to="360 12 12"
                          dur="1s"
                          repeatCount="indefinite"
                        />
                      </path>
                    </svg>
                    {language === "ar" ? "جار تحديث البيانات..." : "Updating data..."}
                  </div>
                ) : (
                  language === "ar" ? "حجز الوحدة" : "Reserve Unit"
                )}
              </button>
            )}
          </div>

          {/* Right Side - Image and Info */}
          <div
            className="card-right"
            style={{
              backgroundImage: `url(${currentParcelData.image_url || currentProjectData?.project_image_desktop_url || '/images/default-unit.jpg'})`
            }}
          >
            {/* Top Section */}
            <div className="top-section" dir="ltr">
              {/* Logo Container */}
              <div className="logo-container">
                {currentProjectData?.developer_logo_url && (
                  <img
                    src={currentProjectData.developer_logo_url}
                    alt="Developer Logo"
                    className="developer-logo"
                  />
                )}
                <div className="rating-badge" dir="ltr">
                  <svg className="star-icon" width="12" height="12" viewBox="0 0 24 24" fill="none" style={{ filter: 'grayscale(100%) brightness(0.5)' }}>
                    <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
                  </svg>
                  <span>{currentParcelData.rating || "4.5"}</span>
                </div>
              </div>

              {/* Share Button */}
              <div className="share-button" onClick={() => onShare && onShare(parcelData)}>
                <img src="/share white v2.png" className="share-icon" alt="share" />
              </div>
            </div>

            {/* Location Info */}
            <div className="location-info">
              <p>
                {language === "ar"
                  ? (currentProjectData?.name_ar || currentProjectData?.name || "المشروع")
                  : (currentProjectData?.name_en || currentProjectData?.name || "Project")
                }
              -
                {currentProjectData?.city?.name_ar || currentProjectData?.city?.name_en || "-"}
              </p>
              <p className="location-date">
                {currentParcelData.completion_date || currentProjectData?.completion_date || ""}
              </p>
            </div>

            {/* Amenities Container   buttons */}
            <div className="amenities-container">

             <button
                          className="favorite-btn"
                          onClick={handleFavoriteClick}
                          title={isFavorited ?
                            (getTranslation("removeFromFavorites", language) || "Remove from Favorites") :
                            (getTranslation("addToFavorites", language) || "Add to Favorites")
                          }
                          // disabled={isLoading || isLoadingFavorite}
                        >
                          {isLoadingFavorite ? (
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                              <circle cx="12" cy="12" r="10" stroke="white" strokeWidth="2" fill="none" opacity="0.3"/>
                              <path d="M12 2 A10 10 0 0 1 22 12" stroke="white" strokeWidth="2" fill="none">
                                <animateTransform
                                  attributeName="transform"
                                  attributeType="XML"
                                  type="rotate"
                                  from="0 12 12"
                                  to="360 12 12"
                                  dur="1s"
                                  repeatCount="indefinite"
                                />
                              </path>
                            </svg>
                          ) : (
                            <HeartIcon
                              filled={isFavorited}
                              size={20}
                              color={isFavorited ? "#dc2626" : "white"}
                            />
                          )}
                        </button>
              {(currentParcelData?.unit_virtual_tour_url || currentProjectData?.virtual_tour_url) &&  (


                      <button
                          className="share-icon"
                          onClick={handleVirtualTourClick}
                          title={(currentParcelData?.unit_virtual_tour_url || currentProjectData?.virtual_tour_url) ?
                            (language === "ar" ? "جولة افتراضية 360°" : "360° Virtual Tour") :
                            (language === "ar" ? "لا توجد جولة افتراضية متاحة" : "No virtual tour available")
                          }
                        >
                          <Lottie
                            animationData={tour360Animation}
                            loop={true}
                            autoplay={true}
                            style={{ width: 30, height: 30,  }}
                          />
                        </button>
                    )}

              {(currentParcelData?.unit_video_url || currentProjectData?.promotional_video_url) && videoAnimation && (
                      <button
                          className="share-icon"
                          onClick={handleUnitVideoClick}
                          title={(currentParcelData?.unit_video_url || currentProjectData?.promotional_video_url) ?
                            (language === "ar" ? "فيديو الوحدة" : "Unit Video") :
                            (language === "ar" ? "لا يوجد فيديو متاح" : "No video available")
                          }
                        >
                          <Lottie
                            animationData={videoAnimation}
                            loop={true}
                            autoplay={true}
                            style={{ width: 30, height: 30 }}
                          />
                        </button>
                    )}

               {currentParcelData?.blueprint_3d_url && blueprint3DAnimation && (

                          <button
                            className="share-icon"
                            onClick={handle3DBlueprintClick}
                            title={currentParcelData?.blueprint_3d_url ?
                              (language === "ar" ? "مخطط ثلاثي الأبعاد" : "3D Blueprint") :
                              (language === "ar" ? "لا يوجد مخطط ثلاثي الأبعاد متاح" : "No 3D blueprint available")
                            }
                          >
                            <Lottie
                              animationData={blueprint3DAnimation}
                              loop={true}
                              autoplay={true}
                              style={{ width: 30, height: 30 }}
                            />
                          </button>
                        )}

                        {(currentParcelData?.blueprint_2d_url || currentParcelData?.blueprint_2d_image_url) && blueprint2DAnimation &&

                        (
                          <button
                           className="share-icon"
                            onClick={handle2DBlueprintClick}
                            title={(currentParcelData?.blueprint_2d_url || currentParcelData?.blueprint_2d_image_url) ?
                              (language === "ar" ? "مخطط ثنائي الأبعاد" : "2D Blueprint") :
                              (language === "ar" ? "لا يوجد مخطط ثنائي الأبعاد متاح" : "No 2D blueprint available")
                            }
                            // disabled={isLoading || (!currentParcelData?.blueprint_2d_url && !currentParcelData?.blueprint_2d_image_url)}
                          >
                            <Lottie
                              animationData={blueprint2DAnimation}
                              loop={true}
                              autoplay={true}
                              style={{ width: 30, height: 30 }}
                            />
                          </button>
                        )
                        }

                        {currentParcelData?.unit_photo_gallery_image_urls && currentParcelData.unit_photo_gallery_image_urls.length > 0 && (
                          <button
                            className="share-icon"
                            onClick={handlePhotoGalleryClick}
                            title={currentParcelData.unit_photo_gallery_image_urls && currentParcelData.unit_photo_gallery_image_urls.length > 0 ?
                              (language === "ar" ? "معرض الصور" : "Photo Gallery") :
                              (language === "ar" ? "لا توجد صور متاحة" : "No photos available")
                            }
                          >
                            <Lottie
                              animationData={photoGalleryAnimation}
                              loop={true}
                              autoplay={true}
                              style={{ width: 30, height: 30 }}
                            />
                          </button>
                        )}

                        {(currentParcelData?.contract_document_url || currentParcelData?.contract_document) && (
                          <button
                            className="share-icon"
                            onClick={handleContractDocumentClick}
                            title={(currentParcelData.contract_document_url || currentParcelData.contract_document) ?
                              (language === "ar" ? "مستند العقد" : "Contract Document") :
                              (language === "ar" ? "لا يوجد مستند عقد متاح" : "No contract document available")
                            }
                          >
                            <img src="/icons/contract-white.png" alt="Contract" style={{ width: 20, height: 20 }} />
                          </button>
                        )}
              {/* <div className="amenity-icon">
                <img src="/map_icons/Mosque icon v2.png" className="amenity-icon-img" alt="mosque" />
              </div>
              <div className="amenity-icon">
                <img src="/map_icons/School icon v2.png" className="amenity-icon-img" alt="school" />
              </div>
              <div className="amenity-icon">
                <img src="/map_icons/Parking icon v2.png" className="amenity-icon-img" alt="parking" />
              </div>
              <div className="amenity-icon">
                <img src="/map_icons/Green Area icon v2.png" className="amenity-icon-img" alt="garden" />
              </div> */}
            </div>
          </div>
        </div>
      </div>

      {/* Iframe Modal - Moved outside the modal container for full screen size */}
      <IframeModal
        isOpen={iframeModalOpen}
        onClose={handleCloseIframeModal}
        url={iframeUrl}
        title={iframeTitle}
        language={language}
      />

      {/* Photo Gallery Modal */}
      <PhotoGalleryModal
        isOpen={photoGalleryOpen}
        onClose={handleClosePhotoGallery}
        images={currentParcelData?.unit_photo_gallery_image_urls || []}
        projectName={language === "ar"
          ? (currentProjectData?.name_ar || currentProjectData?.name || "المشروع")
          : (currentProjectData?.name_en || currentProjectData?.name || "Project")
        }
        language={language}
      />

      {/* Blueprint Gallery Modal */}
      <PhotoGalleryModal
        isOpen={blueprintGalleryOpen}
        onClose={handleCloseBlueprintGallery}
        images={blueprintImages}
        projectName={blueprintTitle}
        language={language}
      />
    </div>
  );
};

export default ParcelDetailsModal;
